'use client';

import { memo, useMemo } from 'react';
import { Box, Stack, Typography, Chip, IconButton, alpha, Divider } from '@mui/material';
import {
  Circle,
  LocationOn,
  Phone,
  ChevronRight,
  AccessTime,
  Scale,
  Person,
  CalendarToday,
  Inventory2,
} from '@mui/icons-material';
import { themeColors } from '../theme/theme';
import { STATUS, TEXT, BACKGROUND, BORDER, BRAND } from '@/constants/colors';
import { formatDate } from '@/utils/formatters';
import BulkBadge from '@/components/BulkBadge';
import { useTranslation } from '@/kit/useTranslation';

/**
 * CompactParcelCard - Enhanced parcel card with more info
 * Shows essential delivery info at a glance
 * Clean, modern design
 *
 * Uses backend field names (parcel* prefix) as primary source
 */
const CompactParcelCard = memo(({ booking, onClick }) => {
  const { t } = useTranslation();
  // Use backend field names as primary source (parcel* prefix from API)
  const id = booking.parcelId || booking.id;
  const status = booking.parcelStatus || booking.status;
  const price = booking.parcelPrice || booking.price;
  const isMultiStage = booking.parcelIsMultiStage || booking.isMultiStage;
  const currentLeg = booking.parcelCurrentLegId || booking.currentLegSequence || 1;
  const totalLegs = booking.parcelTotalLegs || booking.totalLegs || 1;
  const weight = booking.parcelWeightKg || booking.parcelWeight || booking.weight;
  const itemType = booking.parcelParcelType || booking.itemType;
  const createdAt = booking.parcelCreatedAt || booking.createdAt;
  const receiverName = booking.parcelDeliveryAddress?.name || booking.receiverName;
  const receiverPhone = booking.parcelDeliveryAddress?.phone || booking.receiverPhone;
  const description = booking.parcelDescription || booking.description;

  // Bulk order data
  const bulkOrderId = booking.bulkOrderId || booking.bulk_order_id;
  const bulkOrderReference = booking.bulkOrderReference || booking.bulk_order_reference;
  const bulkItemCount = booking.bulkItemCount || booking.bulk_item_count;

  // Admin creator data
  const createdViaAdmin = booking.created_via_admin_console || booking.createdViaAdminConsole || false;
  const createdByAdminUserId = booking.created_by_admin_user_id || booking.createdByAdminUserId;

  // Format ID for display
  const displayId = id ? String(id) : null;

  // Using formatDate from @/utils/formatters

  const getAddressLabel = (address) => {
    if (!address) return t('common.na');
    if (typeof address === 'string') return address;

    // Try fullAddress first (most complete)
    if (address.fullAddress && address.fullAddress.trim()) return address.fullAddress;

    // Try to build from available fields
    const parts = [];
    if (address.area && address.area.trim()) parts.push(address.area);
    if (address.city && address.city.trim()) parts.push(address.city);

    if (parts.length > 0) return parts.join(', ');

    // Fallback to label or address field
    if (address.label && address.label.trim()) return address.label;
    if (address.address && address.address.trim()) return address.address;

    // Last resort: check for coordinates
    if (address.latitude && address.longitude) {
      return `${address.latitude.toFixed(4)}, ${address.longitude.toFixed(4)}`;
    }

    return t('common.na');
  };

  const getShortAddress = (addr, maxLen = 30) => {
    const full = getAddressLabel(addr);
    if (full.length > maxLen) return full.substring(0, maxLen) + '...';
    return full;
  };

  // Use backend field names for addresses
  const fromLabel = getShortAddress(booking.parcelPickupAddress);
  const toLabel = getShortAddress(booking.parcelDeliveryAddress);
  const riderName = booking.parcelRiderName || booking.riderName;
  const riderId = booking.parcelRiderId;
  const riderPhone = booking.parcelRiderPhone || booking.riderPhone;

  // Status colors
  const statusConfig = useMemo(() => {
    const configs = {
      PENDING: {
        bg: STATUS.pending.bg,
        color: STATUS.pending.text,
        label: t('parcel.statusPending'),
      },
      ASSIGNED: {
        bg: STATUS.assigned.bg,
        color: STATUS.assigned.text,
        label: t('parcel.statusAssigned'),
      },
      PICKED_UP: { bg: '#ede9fe', color: '#6d28d9', label: t('parcel.statusPickedUp') },
      IN_TRANSIT: { bg: '#e0e7ff', color: '#4338ca', label: t('parcel.statusInTransit') },
      OUT_FOR_DELIVERY: {
        bg: '#cffafe',
        color: '#0e7490',
        label: t('parcel.statusOutForDelivery'),
      },
      DELIVERED: {
        bg: STATUS.delivered.bg,
        color: STATUS.delivered.text,
        label: t('parcel.statusDelivered'),
      },
      COMPLETED: {
        bg: STATUS.delivered.bg,
        color: STATUS.delivered.text,
        label: t('parcel.statusCompleted'),
      },
      CANCELLED: {
        bg: STATUS.cancelled.bg,
        color: STATUS.cancelled.text,
        label: t('parcel.statusCancelled'),
      },
    };
    return (
      configs[status?.toUpperCase()] || {
        bg: BACKGROUND.subtle,
        color: TEXT.secondary,
        label: status,
      }
    );
  }, [status]);

  const isActive = ['ASSIGNED', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(
    status?.toUpperCase()
  );

  return (
    <Box
      onClick={() => onClick?.(booking)}
      sx={{
        bgcolor: BACKGROUND.paper,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: isActive ? alpha(statusConfig.color, 0.2) : BORDER.light,
        p: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        '&:hover': {
          borderColor: alpha(themeColors.primaryColor, 0.3),
          boxShadow: `0 4px 12px ${alpha(themeColors.primaryColor, 0.08)}`,
          transform: 'translateY(-1px)',
        },
      }}
    >
      {/* Header Row */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          {displayId && (
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: TEXT.primary }}>
              #{displayId}
            </Typography>
          )}
          {price && (
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#10b981' }}>
              ₹{Number(price).toFixed(2)}
            </Typography>
          )}
          {isMultiStage && totalLegs > 1 && (
            <Chip
              label={`Leg ${currentLeg}/${totalLegs}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: '#f3e8ff',
                color: BRAND.accent,
              }}
            />
          )}
          {bulkOrderId && bulkOrderReference && (
            <BulkBadge
              bulkOrderId={bulkOrderId}
              bulkOrderReference={bulkOrderReference}
              itemCount={bulkItemCount}
              compact
            />
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            label={statusConfig.label}
            size="small"
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 700,
              bgcolor: statusConfig.bg,
              color: statusConfig.color,
              borderRadius: 1.5,
            }}
          />
          {createdViaAdmin && (
            <Chip
              label="Admin"
              size="small"
              sx={{
                height: 24,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: '#fef3c7',
                color: '#b45309',
                borderRadius: 1.5,
              }}
            />
          )}
        </Stack>
      </Stack>

      {/* Route Row - Enhanced with full addresses */}
      <Box sx={{ mb: 1.5, position: 'relative', pl: 2 }}>
        {/* Vertical line connecting dots */}
        <Box
          sx={{
            position: 'absolute',
            left: 5,
            top: 10,
            bottom: 10,
            width: 2,
            bgcolor: BORDER.light,
            borderRadius: 1,
          }}
        />

        {/* From */}
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
          <Circle
            sx={{
              fontSize: 10,
              color: STATUS.success,
              mt: 0.5,
              ml: -1.3,
              zIndex: 1,
              bgcolor: BACKGROUND.paper,
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: '0.8rem',
                color: TEXT.primary,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {fromLabel}
            </Typography>
          </Box>
        </Stack>

        {/* To */}
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <LocationOn
            sx={{
              fontSize: 14,
              color: STATUS.error,
              mt: 0.3,
              ml: -1.5,
              zIndex: 1,
              bgcolor: BACKGROUND.paper,
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: '0.8rem',
                color: TEXT.primary,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {toLabel}
            </Typography>
            {receiverName && (
              <Typography sx={{ fontSize: '0.7rem', color: TEXT.secondary, mt: 0.25 }}>
                <Person sx={{ fontSize: 11, mr: 0.5, verticalAlign: 'middle' }} />
                {receiverName}
                {receiverPhone && ` • ${receiverPhone}`}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Info chips row */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5, gap: 0.5 }}>
        {createdAt && (
          <Chip
            icon={<CalendarToday sx={{ fontSize: '12px !important' }} />}
            label={formatDate(createdAt)}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.65rem',
              bgcolor: BACKGROUND.subtle,
              color: TEXT.secondary,
              '& .MuiChip-icon': { color: TEXT.muted },
            }}
          />
        )}
        {weight && (
          <Chip
            icon={<Scale sx={{ fontSize: '12px !important' }} />}
            label={`${weight} kg`}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.65rem',
              bgcolor: BACKGROUND.subtle,
              color: TEXT.secondary,
              '& .MuiChip-icon': { color: TEXT.muted },
            }}
          />
        )}
        {itemType && (
          <Chip
            icon={<Inventory2 sx={{ fontSize: '12px !important' }} />}
            label={
              Array.isArray(itemType)
                ? itemType.join(', ')
                : typeof itemType === 'string'
                  ? itemType
                  : t('common.item')
            }
            size="small"
            sx={{
              height: 22,
              fontSize: '0.65rem',
              bgcolor: BACKGROUND.subtle,
              color: TEXT.secondary,
              '& .MuiChip-icon': { color: TEXT.muted },
              maxWidth: 120,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        )}
      </Stack>

      {/* Divider */}
      <Divider sx={{ mb: 1.5 }} />

      {/* Footer Row */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
        >
          {/* Rider info */}
          {(riderName || riderId) && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ minWidth: 0, overflow: 'hidden' }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1.5,
                  bgcolor: BRAND.accent,
                  color: TEXT.inverse,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {(riderName || riderId)?.toString()?.charAt(0)?.toUpperCase() || 'R'}
              </Box>
              <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: TEXT.primary,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {riderName || t('parcel.riderWithId', { id: riderId })}
                </Typography>
              </Box>
              {riderPhone && (
                <IconButton
                  size="small"
                  component="a"
                  href={`tel:${riderPhone}`}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ p: 0.25, color: BRAND.accent, flexShrink: 0 }}
                >
                  <Phone sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Stack>
          )}

          {/* No rider assigned */}
          {!riderName && !riderId && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: TEXT.muted,
                fontStyle: 'italic',
              }}
            >
              {t('common.awaitingRider')}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {/* Price */}
          {price && (
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: STATUS.success }}>
              ₹{price}
            </Typography>
          )}
          <ChevronRight sx={{ fontSize: 20, color: TEXT.muted }} />
        </Stack>
      </Stack>
    </Box>
  );
});

CompactParcelCard.displayName = 'CompactParcelCard';

export { CompactParcelCard };
export default CompactParcelCard;
